import React, {useEffect, useRef, useState} from 'react';
import {Text, View, Modal, TouchableWithoutFeedback} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import useGetOrderDetailsDetailsApi from '@/Hooks/SubscriptionDetails/useGetOrderDetails';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import {getSubscriptionBadgeStyle, openInNewTab} from '@/Utils/CommonUtils';
import {useNavigation} from '@react-navigation/native';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import Icon from 'react-native-vector-icons/MaterialIcons';
import styles from './styles';
import {ScreenName} from '@/navigators/StackConstants';
import Spacer from '@/Atoms/Spacer';
import SlideModal from '@/Atoms/SlideModal';
import CCButton from '@/Atoms/CCButton';
// ---------- pure helpers ----------

const formatSimpleDate = (dateStr: string) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${months[parseInt(month, 10) - 1]} ${parseInt(day, 10)}, ${year}`;
};

const BOLD_KEYWORDS = ['Tournaments', 'Mentor'];

const splitBold = (message: string): [string, string] => {
  const numMatch = message.match(/^(\d+)(\s.+)$/);
  if (numMatch) return [numMatch[1], numMatch[2]];
  for (const keyword of BOLD_KEYWORDS) {
    if (message === keyword) return [message, ''];
    if (message.startsWith(keyword + ' '))
      return [keyword, message.slice(keyword.length)];
  }
  const wordMatch = message.match(/^(.*?)(\s(?:every|after|in|per|a\s).+)$/);
  if (wordMatch?.[1]) return [wordMatch[1], wordMatch[2]];
  return ['', message];
};

interface Props {
  userId: string;
  orderId: string;
  refreshToken?: number;
  setActiveProgressReports: (reports: any[]) => void;
  setShowProgressReportModal: (v: boolean) => void;
  handleResumeSubscription: (orderId: string) => void;
  setPauseOrderId: (id: string) => void;
  setPauseEndDate: (date: Date | null) => void;
  setPauseError: (err: string) => void;
  setPauseLimits: (limits: any) => void;
  setPauseLimitsError: (err: string) => void;
  setShowPauseModal: (v: boolean) => void;
  getPauseLimits: (params: {order_id: string}) => void;
  isMobileOrTab: boolean;
}

const SubscriptionCard = ({
  userId,
  orderId,
  refreshToken,
  setActiveProgressReports,
  setShowProgressReportModal,
  handleResumeSubscription,
  setPauseOrderId,
  setPauseEndDate,
  setPauseError,
  setPauseLimits,
  setPauseLimitsError,
  setShowPauseModal,
  getPauseLimits,
  isMobileOrTab,
}: Props) => {
  const navigation = useNavigation();
  const buttonRef = useRef<View>(null);
  const [openMore, setOpenMore] = useState(false);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [invoice, setInvoice] = useState<any>(null);
  const [planSummary, setPlanSummary] = useState<any>(null);
  const [dropdownPosition, setDropdownPosition] = useState({top: 0, right: 16});
  const [showDocument, setShowDocument] = useState(false);

  const {mutate: getSubscriptionOrderDetails} = useGetOrderDetailsDetailsApi(
    (data: any) => {
      setOrderDetails(data?.order);
      setSubscriptions(data?.subscriptions ?? []);
      setInvoice(data?.invoice);
      setPlanSummary(data?.plan_summary);
    },
    () => {},
  );

  const firstSub = subscriptions?.[0];
  const headerHasProgressReport = firstSub?.ptm?.progress_reports?.length > 0;
  const headerHasCertificate = !!firstSub?.certificate_url;
  const headerHasInvoice = !!(invoice as any)?.invoice_url;
  const orderStatus = (orderDetails?.status || '').toLowerCase();
  const batchType = (
    planSummary?.batch_type ||
    firstSub?.batch?.batch_type ||
    firstSub?.batch_type ||
    ''
  ).toLowerCase();
  const batchIsInactive = firstSub?.batch?.is_active === false;
  const canResume = orderStatus === 'paused' && !batchIsInactive;
  const canPause =
    orderStatus === 'active' &&
    (batchType.includes('personal') || batchType.includes('webinar'));

  const condForMoreOptions =
    headerHasCertificate ||
    headerHasInvoice ||
    headerHasProgressReport ||
    canPause;

  const openPauseModal = () => {
    setPauseOrderId(orderId);
    setPauseEndDate(null);
    setPauseError('');
    setPauseLimits(null);
    setPauseLimitsError('');
    setShowPauseModal(true);
    getPauseLimits({order_id: orderId});
    setOpenMore(false);
  };

  useEffect(() => {
    if (orderId && userId) {
      getSubscriptionOrderDetails({orderId, userId});
    }
  }, [orderId, userId, refreshToken]);

  if (!orderDetails) return null;

  const dropdownItem = (
    borderBottom: boolean,
    icon: string,
    label: string,
    onPress: () => void,
  ) => (
    <Box
      onPress={onPress}
      type="row-flex-start"
      style={[styles.icn, {borderBottomWidth: borderBottom ? 1 : 0}]}>
      <MaterialCommunityIcons
        name={icon as any}
        size={14}
        color={CCColors.TextColor.Dark}
      />
      <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Dark}>
        {label}
      </CCText>
    </Box>
  );

  return (
    <>
      <Modal
        visible={openMore}
        transparent
        animationType="none"
        onRequestClose={() => setOpenMore(false)}>
        <TouchableWithoutFeedback onPress={() => setOpenMore(false)}>
          <View style={{flex: 1}}>
            <TouchableWithoutFeedback>
              <View
                style={[
                  styles.abs,
                  {
                    position: 'absolute',
                    top: dropdownPosition.top,
                    right: dropdownPosition.right,
                    backgroundColor: CCColors?.White,
                    zIndex: 9999,
                    elevation: 9999,
                    shadowColor: CCColors?.Black,
                    shadowOffset: {width: 0, height: 2},
                    shadowOpacity: 0.15,
                    shadowRadius: 4,
                  },
                ]}>
                {headerHasProgressReport &&
                  dropdownItem(
                    true,
                    'chart-bar',
                    'Download Progress Report',
                    () => {
                      setActiveProgressReports(
                        firstSub?.ptm?.progress_reports ?? [],
                      );
                      setShowProgressReportModal(true);
                      setOpenMore(false);
                    },
                  )}
                {headerHasCertificate &&
                  dropdownItem(
                    true,
                    'certificate-outline',
                    'Download Certificate',
                    () => {
                      openInNewTab(firstSub?.certificate_url);
                      setOpenMore(false);
                    },
                  )}
                {headerHasInvoice &&
                  dropdownItem(
                    canPause,
                    'file-document-outline',
                    'Download Invoice',
                    () => {
                      openInNewTab((invoice as any)?.invoice_url);
                      setOpenMore(false);
                    },
                  )}
                {/* {canResume &&
                  dropdownItem(
                    canPause,
                    'play-circle-outline',
                    'Resume a class',
                    () => {
                      handleResumeSubscription(firstOrder);
                      setOpenMore(false);
                    },
                  )} */}
                {canPause &&
                  dropdownItem(
                    false,
                    'pause-circle-outline',
                    'Pause a class',
                    openPauseModal,
                  )}
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
      <SlideModal
        visible={showDocument}
        type={isMobileOrTab ? "bottom" : "center"}
        onClose={() => setShowDocument(false)}
        modalHeaderTitle={"Handbook"}
        modalHeaderRight={
          <Box
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            onPress={() => openInNewTab('https://cc-home.s3.ap-south-1.amazonaws.com/CircleChess_Welcome_Kit.docx.pdf#toolbar=0')}
          >
            <MaterialCommunityIcons name="download" size={24} color={CCColors.Black} />
          </Box>
        }
        customStyle={{ width: '90%', height: '90%', padding: 0 }}
      >
        <View style={{ flex: 1, backgroundColor: CCColors.White, borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ flex: 1, width: '100%', height: '100%' }}>
            <iframe
              src="https://cc-home.s3.ap-south-1.amazonaws.com/CircleChess_Welcome_Kit.docx.pdf#toolbar=0"
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Handbook"
            />
          </div>
        </View>
      </SlideModal>
      <Box flexDirection="column" style={[styles.rtv, { overflow: 'visible' }]}>
        <Box
          flexDirection="row"
          justifyContent="space-between"
          alignItems={isMobileOrTab ? 'flex-start' : 'center'}
          style={styles.btm}>
          <Box gap={isMobileOrTab ? 10 : 4} flexDirection="column">
            <Box gap={8} alignItems="center" type="row-flex-start">
              <CCText fontSize={FontSizes[18]} fontWeight={500}>
                {planSummary?.plan_name ?? orderDetails?.plan_title}
              </CCText>
              <Box
                style={[
                  {
                    backgroundColor: getSubscriptionBadgeStyle(
                      orderDetails?.status,
                    ).backgroundColor,
                  },
                  styles.pad,
                ]}>
                <CCText
                  fontSize={FontSizes[9]}
                  fontWeight={600}
                  color={
                    getSubscriptionBadgeStyle(orderDetails?.status).textColor
                  }>
                  {orderDetails?.status}
                </CCText>
              </Box>
            </Box>
            <Box gap={16} flexDirection={isMobileOrTab ? 'column' : 'row'}>
              <Box type="row-flex-start" style={styles.mnr}>
                <MaterialCommunityIcons
                  name="desktop-mac"
                  size={16}
                  color={CCColors.TextColor.Dark}
                />
                <CCText
                  fontWeight={400}
                  fontSize={FontSizes[14]}
                  color={CCColors.TextColor.Dark}>
                  {planSummary?.batch_type ?? subscriptions?.[0]?.batch_type}
                </CCText>
              </Box>
              <Box type="row-flex-start" style={styles.mnr}>
                <Icon
                  name="access-time"
                  size={16}
                  color={CCColors.TextColor.Dark}
                />
                <CCText
                  fontWeight={400}
                  fontSize={FontSizes[14]}
                  color={CCColors.TextColor.Dark}>
                  {formatSimpleDate(
                    subscriptions?.[0]?.subscription_start_date,
                  )}
                  {' \u2013 '}
                  {formatSimpleDate(subscriptions?.[0]?.subscription_end_date)}
                </CCText>
              </Box>
              <Box type="row-flex-start" style={styles.mnr}>
                <MaterialCommunityIcons
                  name="pound"
                  size={16}
                  color={CCColors.TextColor.Dark}
                />
                <CCText
                  fontWeight={400}
                  fontSize={FontSizes[14]}
                  color={CCColors.TextColor.Dark}>
                  {orderDetails?.order_id}
                </CCText>
              </Box>
              {planSummary?.batch_code ?? subscriptions?.[0]?.class_name ? (
                <Box type="row-flex-start" style={styles.mnr}>
                  <MaterialCommunityIcons
                    name="tag-outline"
                    size={16}
                    color={CCColors.TextColor.Dark}
                  />
                  <CCText
                    fontWeight={400}
                    fontSize={FontSizes[14]}
                    color={CCColors.TextColor.Dark}>
                    {planSummary?.batch_code ?? subscriptions?.[0]?.class_name}
                  </CCText>
                </Box>
              ) : null}
            </Box>
          </Box>
          <Box flexDirection="row" alignItems="center" style={{ gap: 12 }}>
            {isMobileOrTab ? (
              <Box
                onPress={() => {
                  setShowDocument(true);
                }}
                style={styles.bg}
              >
                <MaterialCommunityIcons
                  name="book-open-page-variant"
                  size={16}
                  color={CCColors?.Black}
                />
              </Box>
            ) : (
              <CCButton onPress={() => {setShowDocument(true)}}
                style={{ backgroundColor: '#F0EBE1'}}>
                <CCText fontSize={FontSizes[12]}>Handbook</CCText>
              </CCButton>
            )}
            {canResume && (
              <CCButton
                onPress={() => handleResumeSubscription(orderId)}
                style={{backgroundColor: CCColors.Primary.Default}}>
                <Box type="row-center" style={{gap: 6}}>
                  <MaterialCommunityIcons
                    name="play-circle-outline"
                    size={16}
                    color={CCColors.TextColor.Dark}
                  />
                  <CCText fontSize={FontSizes[12]} fontWeight={600}>
                    Resume
                  </CCText>
                </Box>
              </CCButton>
            )}
            {condForMoreOptions && (
              <View ref={buttonRef}>
                <Box
                  onPress={() => {
                    if (openMore) {
                      setOpenMore(false);
                    } else {
                      buttonRef.current?.measureInWindow(
                        (x, y, width, height) => {
                          setDropdownPosition({ top: y + height + 4, right: 16 });
                          setOpenMore(true);
                        },
                      );
                    }
                  }}
                  style={styles.bg}>
                  <Icon name={'more-vert'} size={16} color={CCColors.Black} />
                </Box>
              </View>
            )}
          </Box>
        </Box>

        {(planSummary?.features?.length > 0 ||
          subscriptions?.[0]?.features?.length > 0) && (
          <Box
            flexDirection={isMobileOrTab ? 'column' : 'row'}
            style={styles.rap}>
            {(planSummary?.features?.length > 0
              ? planSummary?.features
              : subscriptions?.[0]?.features ?? []
            ).map((feature: any) => {
              const iconMap: Record<string, string> = {
                classes: 'monitor',
                assignments: 'clipboard-text-outline',
                ptm: 'calendar-month-outline',
                tournaments: 'trophy-outline',
                psychology_class: 'brain',
              };
              const iconName = iconMap[feature?.type] || 'star-outline';
              const isTournament = feature?.type === 'tournaments';
              return (
                <Box
                  key={feature?.type}
                  flexDirection={isMobileOrTab ? 'column' : 'row'}
                  justifyContent="space-between"
                  alignItems={isMobileOrTab ? 'flex-start' : 'center'}
                  style={[
                    styles.basis,
                    {flexBasis: isMobileOrTab ? undefined : '31.5%'},
                  ]}>
                  <Box type="row-flex-start" style={{gap: 10, flex: 1}}>
                    <Box type="center" style={styles.thirty}>
                      <Box type="center" style={styles.iconbg}>
                        <MaterialCommunityIcons
                          name={iconName as any}
                          size={16}
                          color={CCColors.Black}
                        />
                      </Box>
                    </Box>
                    {(() => {
                      const [bold, rest] = splitBold(feature?.message ?? '');
                      return (
                        <Text style={styles.txt}>
                          {bold ? (
                            <Text style={styles.bold}>{bold}</Text>
                          ) : null}
                          {rest}
                        </Text>
                      );
                    })()}
                  </Box>
                  {isTournament &&
                    (orderDetails?.status === 'Active' ||
                      orderDetails?.status === 'Upcoming') && (
                      <Box
                        onPress={() => {
                          navigation.navigate(ScreenName.Tournaments, {
                            from: 'tournament',
                          });
                        }}
                        style={[
                          styles.new,
                          isMobileOrTab ? {marginTop: 8} : {},
                        ]}>
                        <CCText
                          fontSize={FontSizes[11]}
                          fontWeight="semibold"
                          color={CCColors.TextColor.Dark}>
                          View Tournament
                        </CCText>
                      </Box>
                    )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
      <Spacer spacing={48} />
    </>
  );
};

export default SubscriptionCard;
